import React from "react";

import { UseCanProps, useCan, useResourceParams } from "@refinedev/core";

import { Skeleton, Stack } from "@mui/material";

import { resources } from "@/config/resources";

import { PermissionDeniedPage } from "./PermissionDenied";

/**
 * Protected route wrapper that automatically reads resource metadata
 * and checks permissions using the AccessControlProvider.
 *
 * Usage:
 * ```tsx
 * <Route path="practitioner" element={
 *   <ProtectedRoute>
 *     <Practitioner />
 *   </ProtectedRoute>
 * } />
 * ```
 *
 * The resource name is automatically detected from the route context.
 * For custom resources or actions, pass them explicitly:
 *
 * ```tsx
 * <ProtectedRoute resource="custom-resource" action="manage">
 *   <CustomComponent />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  resource?: string;
  action?: string;
}> = ({ children, resource: resourceProp, action = "list" }) => {
  const { resource } = useResourceParams();

  // Use explicit resource prop or fall back to context

  const resourceName = resourceProp || resource?.name;
  const resourceConfig = resourceName ? resources.find(r => r.name === resourceName) : undefined;
  const requiredRoles = resourceConfig?.meta?.requiredRoles as string[] | undefined;

  const shouldCheckPermissions = !!resourceName && !!requiredRoles && requiredRoles.length > 0;

  const canArgs: UseCanProps = {
    // Use useCan hook to check permissions with AccessControlProvider
    resource: resourceName ?? "", // safe placeholder when disabled
    action,
    params: { showToast: true },
    queryOptions: {
      enabled: shouldCheckPermissions, // no-op when not needed
    },
  };

  const { data: canAccess, isLoading } = useCan(canArgs);

  // If no resource or no role requirements, allow access
  if (!shouldCheckPermissions) {
    return <>{children}</>;
  }

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight="60vh">
        <Skeleton variant="rectangular" width="80%" height={400} />
      </Stack>
    );
  }

  // Show permission denied page if access is not allowed
  if (!canAccess?.can) {
    return <PermissionDeniedPage />;
  }

  // Render protected content
  return <>{children}</>;
};
